import { useCallback, useEffect, useRef, useState } from "react";
import { fechaLlana, numeroLlano, ROTULOS } from "../../../shared/piezas/index.ts";
import type { FuenteAbrible, Hallazgos } from "../../../shared/piezas/index.ts";
import { type ClienteNexo, type Evidencia, type LlaveEvidencia, vizEvidencia } from "../api.ts";
import { FICHA, texto } from "./catalogo.ts";
import { accionDeError, mensajeDeError, razonDe } from "./mensajes.ts";
import { ligaOficial } from "./partes.ts";

/**
 * «Ver la fuente» dentro de Nexo (decisión 3 de Bernardo; diseño 4a §2.9): el mismo visor que Argus Team, con lo que trae el
 * documento y, con la llave del lote 0a, el acto completo por nexo.evidencia (public.nexo_viz_evidencia). La página del acta y las
 * copias guardadas de los documentos oficiales son del corte 4b: aquí se dice «Todavía no podemos mostrar la página del acta.».
 */

interface Props {
  cliente: ClienteNexo;
  fuente: FuenteAbrible;
  /** El documento de la ficha: de él sale lo de la lista de OFAC (`sancionados[ref]`). */
  doc: Hallazgos;
  /** La frase que se citó, cuando la fuente es una lista que no tiene más que mostrar (lo de «También»). */
  frase?: string | null;
  onCerrar(): void;
  onBuscarDeNuevo?(): void;
}

type Estado = { estado: "cargando" } | { estado: "lista"; evidencia: Evidencia } | { estado: "error"; razon: string; mensaje: string };

export function VerFuente({ cliente, fuente, doc, frase, onCerrar, onBuscarDeNuevo }: Props) {
  const raiz = useRef<HTMLElement>(null);
  useEffect(() => { raiz.current?.focus(); }, [fuente]);
  return (
    <section ref={raiz} className="nx-ver-fuente" aria-label={FICHA.laFuente} tabIndex={-1}>
      <div className="nx-ver-fuente-cabeza">
        <h3>{FICHA.laFuente}</h3>
        <button type="button" className="nx-btn" onClick={onCerrar}>{FICHA.cerrar}</button>
      </div>
      {frase ? <p>{frase}</p> : null}
      <Contenido cliente={cliente} fuente={fuente} doc={doc} {...(onBuscarDeNuevo ? { onBuscarDeNuevo } : {})} />
    </section>
  );
}

function Contenido({ cliente, fuente, doc, onBuscarDeNuevo }: { cliente: ClienteNexo; fuente: FuenteAbrible; doc: Hallazgos; onBuscarDeNuevo?(): void }) {
  switch (fuente.tipo) {
    case "acto":
      return <ConLlave cliente={cliente} llave={{ runId: fuente.runId, actoId: fuente.actoId }} {...(onBuscarDeNuevo ? { onBuscarDeNuevo } : {})} />;
    case "acta":
      return <Acta archivo={fuente.documento} pagina={fuente.pagina} fecha={fuente.fecha} cita={fuente.cita}
        verificacion={fuente.verificacion ? { ...fuente.verificacion } : null} nombre={null} />;
    case "lista_ofac": {
      const s = fuente.sancionado ? doc.sancionados[fuente.sancionado] : undefined;
      return (
        <div className="nx-vf-cuerpo">
          <h4>{FICHA.suRegistro}</h4>
          {s ? <p><b>{s.nombre}</b></p> : null}
          {s ? <p>{s.en_lista.texto}</p> : null}
          {s && s.en_lista.programas.length > 0 ? <ul>{s.en_lista.programas.map((p) => <li key={p.clave}>{p.texto}</li>)}</ul> : null}
          {fuente.consultado ? <p className="nx-tenue">{texto(FICHA.copiaConsultada, { fecha: fechaLlana(fuente.consultado) })}</p> : null}
          <Oficial url={fuente.url} />
        </div>
      );
    }
    case "comunicado":
      return (
        <div className="nx-vf-cuerpo">
          <h4>{fuente.fecha ? texto(FICHA.comunicado, { fecha: fechaLlana(fuente.fecha) }) : FICHA.comunicadoSinFecha}</h4>
          {fuente.cita ? <blockquote lang="en">{fuente.cita}</blockquote> : null}
          <Oficial url={fuente.url} />
        </div>
      );
    // Corte 4b: un párrafo con su llave se lee con nexo.evidencia({versionId, fragmentId}) como en 4a; nuestra copia del documento
    // completo, la página del acta y los registros de las listas se abren en Argus Team (Nexo no lee nexo.fuente_datos).
    case "fragmento":
      return <ConLlave cliente={cliente} llave={{ versionId: fuente.versionId, fragmentId: fuente.fragmentId }} {...(onBuscarDeNuevo ? { onBuscarDeNuevo } : {})} />;
    case "documento":
    case "documento_oficial":
      return (
        <div className="nx-vf-cuerpo">
          {fuente.titulo ? <h4>{fuente.titulo}</h4> : null}
          {fuente.fecha ? <p className="nx-tenue">{fechaLlana(fuente.fecha)}</p> : null}
          <Oficial url={fuente.url} />
        </div>
      );
    // La lista del SAT en 4a: la frase de «También» (arriba) y la fecha; sin liga (diseño §2.9).
    case "lista_sat":
      return <div className="nx-vf-cuerpo">{fuente.fecha ? <p className="nx-tenue">{fechaLlana(fuente.fecha)}</p> : null}</div>;
    // Un tipo de fuente que esta pantalla no conoce: la ficha no ofrece abrirlo (corte 0).
    case "no_se_abre":
      return null;
  }
}

/** Lo que se lee con la llave: el acto completo o el párrafo de un comunicado. Cambios de la corrida y faltantes, dentro del visor. */
function ConLlave({ cliente, llave, onBuscarDeNuevo }: { cliente: ClienteNexo; llave: LlaveEvidencia; onBuscarDeNuevo?(): void }) {
  const [estado, setEstado] = useState<Estado>({ estado: "cargando" });
  const [intento, setIntento] = useState(0);
  const clave = JSON.stringify(llave);
  const cargar = useCallback((signal: AbortSignal) => {
    setEstado({ estado: "cargando" });
    vizEvidencia(cliente, JSON.parse(clave) as LlaveEvidencia, signal).then(
      (evidencia) => { if (!signal.aborted) setEstado({ estado: "lista", evidencia }); },
      (e: unknown) => {
        if (signal.aborted) return;
        const razon = razonDe(e);
        setEstado({ estado: "error", razon, mensaje: mensajeDeError(razon) });
      },
    );
  }, [cliente, clave]);
  useEffect(() => {
    const control = new AbortController();
    cargar(control.signal);
    return () => { control.abort(); };
  }, [cargar, intento]);

  if (estado.estado === "cargando") return <div className="nx-esqueleto" aria-busy="true" />;
  if (estado.estado === "error") {
    const accion = accionDeError(estado.razon);
    return (
      <p className="nx-error" role="alert">{estado.mensaje}{" "}
        {accion === "buscar" && onBuscarDeNuevo ? <button type="button" className="nx-btn" onClick={onBuscarDeNuevo}>{FICHA.buscarDeNuevo}</button> : null}
        {accion === "reintentar" ? <button type="button" className="nx-btn" onClick={() => { setIntento((n) => n + 1); }}>{FICHA.reintentar}</button> : null}
      </p>
    );
  }
  const ev = estado.evidencia;
  if (ev.tipo === "comunicado") {
    return (
      <div className="nx-vf-cuerpo">
        <h4>{ev.documento.fecha ? texto(FICHA.comunicado, { fecha: fechaLlana(ev.documento.fecha) }) : FICHA.comunicadoSinFecha}</h4>
        {ev.documento.titulo ? <p><b>{ev.documento.titulo}</b></p> : null}
        {!ev.version_vigente ? <p className="nx-aviso">{FICHA.noVigente}</p> : null}
        {ev.traduccion ? <><p className="nx-tenue">{ROTULOS.traduccion}</p><blockquote>{ev.traduccion.texto_es}</blockquote></> : null}
        <details><summary>{ROTULOS.leerOriginal}</summary><blockquote lang="en">{ev.fragmento.texto}</blockquote></details>
        <Oficial url={ev.documento.url ?? null} />
      </div>
    );
  }
  const a = ev.acto;
  return (
    <Acta archivo={a.archivo ?? null} pagina={a.pagina ?? null} fecha={a.fecha ?? null} cita={a.cita ?? null}
      verificacion={a.verificacion ?? null} nombre={a.persona?.nombre ?? null}
      acto={{ papel: a.papel_texto ?? null, persona: a.persona?.nombre ?? null, sociedad: a.sociedad?.nombre ?? null, instrumento: a.fecha_instrumento ?? null,
        inscripcion: a.fecha_inscripcion ?? null, acciones: a.acciones ?? null, total: a.total_acciones ?? null, impreso: a.porcentaje_impreso ?? null }} />
  );
}

interface ActoVisible {
  papel: string | null; persona: string | null; sociedad: string | null; instrumento: string | null; inscripcion: string | null;
  acciones: number | null; total: number | null; impreso: number | null;
}

/** Un acta: documento, página, fecha, lo que dice y sus tres comprobaciones en sus tres valores (texto 42). */
function Acta({ archivo, pagina, fecha, cita, verificacion, nombre, acto }: {
  archivo: string | null; pagina: number | null; fecha: string | null; cita: string | null;
  verificacion: { texto_presente: boolean | null; ubicacion: boolean | null; atribucion: boolean | null } | null; nombre: string | null; acto?: ActoVisible;
}) {
  const comprobacion = (valor: boolean | null | undefined, si: string, no: string) => (valor === true ? si : valor === false ? no : FICHA.sinComprobar);
  const quien = nombre ?? "";
  return (
    <div className="nx-vf-cuerpo">
      <h4>{FICHA.actaRpc}</h4>
      <p className="nx-tenue">{[archivo, pagina ? texto(FICHA.pagina, { n: pagina }) : null].filter(Boolean).join(" · ")}</p>
      {acto && (acto.persona || acto.papel || acto.sociedad) ? <p>{[acto.persona, acto.papel, acto.sociedad].filter(Boolean).join(" · ")}</p> : null}
      <dl className="nx-vf-datos">
        {fecha ? <div><dt>{FICHA.fechaActa}</dt><dd>{fechaLlana(fecha)}</dd></div> : null}
        {acto?.instrumento ? <div><dt>{FICHA.fechaInstrumento}</dt><dd>{fechaLlana(acto.instrumento)}</dd></div> : null}
        {acto?.inscripcion ? <div><dt>{FICHA.fechaInscripcion}</dt><dd>{fechaLlana(acto.inscripcion)}</dd></div> : null}
      </dl>
      {acto && acto.acciones !== null ? (
        <p>{acto.total !== null ? texto(FICHA.accionesDe, { n: numeroLlano(acto.acciones), m: numeroLlano(acto.total) }) : texto(ROTULOS.accionesSinTotal, { n: numeroLlano(acto.acciones) })}</p>
      ) : null}
      {acto && acto.impreso !== null ? <p>{texto(FICHA.porcentajeImpreso, { n: numeroLlano(acto.impreso) })}</p> : null}
      {cita ? <><p className="nx-tenue">{FICHA.diceActa}</p><blockquote>{cita}</blockquote></> : null}
      {verificacion ? (
        <ul className="nx-vf-comprobaciones">
          <li className={verificacion.texto_presente === false ? "nx-aviso" : undefined}>{comprobacion(verificacion.texto_presente, FICHA.textoEsta, FICHA.textoNoEsta)}</li>
          <li className={verificacion.ubicacion === false ? "nx-aviso" : undefined}>{comprobacion(verificacion.ubicacion, FICHA.paginaCorrecta, FICHA.paginaIncorrecta)}</li>
          <li className={verificacion.atribucion === false ? "nx-aviso" : undefined}>
            {verificacion.atribucion === null || !quien ? FICHA.sinComprobar : texto(verificacion.atribucion ? FICHA.actaNombra : FICHA.actaNoNombra, { nombre: quien })}
          </li>
        </ul>
      ) : null}
      <p className="nx-tenue">{FICHA.sinPagina}</p>
    </div>
  );
}

/** La liga oficial, como complemento (la copia guardada es del corte 4b). Sólo ligas https. */
function Oficial({ url }: { url: string | null }) {
  const liga = ligaOficial(url);
  if (!liga) return null;
  return <p><a href={liga} target="_blank" rel="noreferrer">{FICHA.abrirOficial}</a></p>;
}
